import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import API from '../api';
import { AuthBrand, AuthCard } from './AuthShell';

export function VerifyEmailPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [verifyForm, setVerifyForm] = useState({ email: location.state?.email || '', code: '' });
  const [verifyError, setVerifyError] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);

  const handleVerify = async (event) => {
    event.preventDefault();
    setIsVerifying(true);
    try {
      await API.post('/verify-email-code/', verifyForm);
      navigate('/login', {
        state: { successMessage: 'Inscription réussie ! Vous pouvez maintenant vous connecter.' },
      });
    } catch (err) {
      console.error(err);
      setVerifyError(err.response?.data?.error || 'Verification failed');
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h2>Vérifier votre email</h2>
        {verifyError && <div className="error-message">{verifyError}</div>}
        <p>Un code de vérification a été envoyé à l'adresse <strong>{verifyForm.email}</strong>. Entrez le code ci-dessous pour vérifier votre compte.</p>
        <form onSubmit={handleVerify}>
          <input
            name="code"
            type="text"
            placeholder="Code de vérification"
            value={verifyForm.code}
            onChange={(event) => setVerifyForm({ ...verifyForm, code: event.target.value })}
            required
          />
          <button type="submit" disabled={isVerifying}>Vérifier</button>
        </form>
        <p>
          Vous avez déjà un code ?
          <button className="switch-btn" onClick={() => navigate('/login')}>Se connecter</button>
        </p>
      </div>
    </div>
  );
}

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [resetEmail, setResetEmail] = useState('');
  const [resetError, setResetError] = useState(null);
  const [resetSuccess, setResetSuccess] = useState(null);
  const [isResetting, setIsResetting] = useState(false);
  const [codeRequested, setCodeRequested] = useState(false);
  const [codeVerified, setCodeVerified] = useState(false);
  const [resetCode, setResetCode] = useState('');
  const [isVerifyingCode, setIsVerifyingCode] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [confirmError, setConfirmError] = useState(null);
  const [confirmSuccess, setConfirmSuccess] = useState(null);
  const [isConfirming, setIsConfirming] = useState(false);

  const handlePasswordResetRequest = async (event) => {
    event.preventDefault();
    setResetError(null);
    setResetSuccess(null);
    setIsResetting(true);
    try {
      await API.post('/reset-password/', { email: resetEmail });
      setResetSuccess('Code de réinitialisation envoyé. Vérifiez votre boîte de réception.');
      setCodeRequested(true);
    } catch (err) {
      console.error(err);
      setResetError(err.response?.data?.error || "Erreur lors de l'envoi de l'email de réinitialisation");
    } finally {
      setIsResetting(false);
    }
  };

  const handleVerifyResetCode = async (event) => {
    event.preventDefault();
    setResetError(null);
    setResetSuccess(null);
    setIsVerifyingCode(true);
    try {
      await API.post('/reset-password-verify/', { email: resetEmail, code: resetCode });
      setResetSuccess('Code vérifié. Entrez votre nouveau mot de passe.');
      setCodeVerified(true);
    } catch (err) {
      console.error(err);
      setResetError(err.response?.data?.error || 'Code invalide ou expiré');
    } finally {
      setIsVerifyingCode(false);
    }
  };

  const handlePasswordResetConfirm = async (event) => {
    event.preventDefault();
    setConfirmError(null);
    setConfirmSuccess(null);
    setIsConfirming(true);
    try {
      await API.post('/reset-password-confirm/', {
        email: resetEmail,
        code: resetCode,
        new_password: newPassword,
        confirm_password: confirmPassword,
      });
      setConfirmSuccess('Mot de passe réinitialisé avec succès. Vous pouvez maintenant vous connecter.');
    } catch (err) {
      console.error(err);
      setConfirmError(err.response?.data?.error || 'Erreur lors de la réinitialisation du mot de passe');
    } finally {
      setIsConfirming(false);
    }
  };

  if (!codeRequested) {
    return (
      <AuthCard title="Mot de passe oublié" description="Entrez votre adresse email pour recevoir un code de réinitialisation.">
        {resetError && <div className="error-message">{resetError}</div>}
        {resetSuccess && <div className="settings-message">{resetSuccess}</div>}
        <form onSubmit={handlePasswordResetRequest}>
          <input name="email" type="email" placeholder="Votre adresse email" value={resetEmail} onChange={(event) => setResetEmail(event.target.value)} required />
          <button type="submit" disabled={isResetting}>{isResetting ? 'Envoi en cours...' : 'Envoyez le code'}</button>
        </form>
        <p><button className="switch-btn" onClick={() => navigate('/login')}>Se connecter</button></p>
      </AuthCard>
    );
  }

  if (!codeVerified) {
    return (
      <AuthCard title="Entrer le code de vérification">
        {resetError && <div className="error-message">{resetError}</div>}
        {resetSuccess && <div className="settings-message">{resetSuccess}</div>}
        <form onSubmit={handleVerifyResetCode}>
          <input name="code" type="text" placeholder="Code de vérification" value={resetCode} onChange={(event) => setResetCode(event.target.value)} required />
          <button type="submit" disabled={isVerifyingCode}>{isVerifyingCode ? 'Vérification...' : 'Vérifier le code'}</button>
        </form>
        <p><button className="switch-btn" onClick={() => navigate('/login')}>Se connecter</button></p>
      </AuthCard>
    );
  }

  return (
    <AuthCard title="Entrer votre nouveau mot de passe">
      {confirmError && <div className="error-message">{confirmError}</div>}
      {confirmSuccess && <div className="settings-message">{confirmSuccess}</div>}
      <form onSubmit={handlePasswordResetConfirm}>
        <input name="new_password" type="password" placeholder="Nouveau mot de passe" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} required />
        <input name="confirm_password" type="password" placeholder="Confirmer le mot de passe" value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} required />
        <button type="submit" disabled={isConfirming}>{isConfirming ? 'Réinitialisation...' : 'Réinitialiser le mot de passe'}</button>
      </form>
      <p><button className="switch-btn" onClick={() => navigate('/login')}>Se connecter</button></p>
    </AuthCard>
  );
}

export function ClosedAccountPage() {
  const navigate = useNavigate();

  return (
    <div className="auth-container">
      <div className="auth-card">
        <AuthBrand />
        <h2 className="closed-account-title">Compte fermé</h2>
        <p>Ce compte a été fermé suite à une détection de fraude.</p>
        <p>Aucun remboursement ne peut être effectué.</p>
        <p>Les fonds seront retournés au numéro depuis lequel le dépôt a été effectué.</p>
        <div className="auth-links">
          <button className="switch-btn" onClick={() => navigate('/login')}>Retour à la connexion</button>
        </div>
      </div>
    </div>
  );
}
